/**
 * Spring's LALE — a tulip — in the shared 120×120 LINE asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw this.
 *
 * WHY A TULIP, AFTER TWO OTHER FLOWERS
 *
 * The theme's glyph was a bellflower, which NODDED, and a nodding flower reads as
 * a sad one (owner 2026-08-16). Lifting the head far enough to look cheerful
 * turns a campanula into something else on the way, so the subject had to move.
 * The first replacement was a six-petalled face-on flower and the owner's verdict
 * was blunt and correct: it was ugly. A ring of blades around a dot is a
 * pictogram, not a plant.
 *
 * A tulip answers all of it. It is the flower that means SPRING — the one that
 * comes up first and the one people photograph fields of — it stands upright by
 * nature so there is no droop to design around, and it famously comes in every
 * colour, which is exactly what a four-digit passcode ramp needs.
 *
 * EVERY SUBPATH IS WOUND CLOCKWISE, BY CONSTRUCTION
 *
 * The reason this module is built out of `poly()` rather than hand-written path
 * strings. The bellflower's leaf was written by hand, came out counter-clockwise,
 * and a nonzero fill SUBTRACTS a reversed subpath wherever it laps another — so
 * the plant showed transparent bites at every joint, twice, before it was found.
 * Sampling each shape into a polygon and forcing its winding makes that class of
 * bug unreachable rather than merely fixed.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THREE PETALS WITH TWO NOTCHES. That silhouette is the entire tulip. One lobe
 *    is an egg, two is a heart, four starts to read as a crown.
 *  - THE CUP IS TALLER THAN IT IS WIDE, and its sides come IN at the bottom. A
 *    cup as wide as it is tall is a crocus; straight sides make it a bucket.
 *  - THE STEM IS STRAIGHT. A tulip's stem is the straightest thing in a garden,
 *    and it is half of why the flower reads as upright and awake.
 *  - TWO LONG BLADE LEAVES, low and sweeping OUT. They are what stops the drawing
 *    being a lollipop, and they are the part that says the plant grew.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon, always wound CLOCKWISE (y down), whichever order it arrives in. */
function poly( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/** A smooth closed curve through the given points (Catmull-Rom), sampled. */
function smooth( ctrl: [ number, number ][], per = 8 ): [ number, number ][] {
  const n = ctrl.length;
  const out: [ number, number ][] = [];
  for ( let i = 0; i < n; i++ ) {
    const p0 = ctrl[ ( i - 1 + n ) % n ], p1 = ctrl[ i ];
    const p2 = ctrl[ ( i + 1 ) % n ], p3 = ctrl[ ( i + 2 ) % n ];
    for ( let k = 0; k < per; k++ ) {
      const t = k / per, t2 = t * t, t3 = t2 * t;
      out.push( [
        0.5 * ( ( 2 * p1[ 0 ] ) + ( -p0[ 0 ] + p2[ 0 ] ) * t
          + ( 2 * p0[ 0 ] - 5 * p1[ 0 ] + 4 * p2[ 0 ] - p3[ 0 ] ) * t2
          + ( -p0[ 0 ] + 3 * p1[ 0 ] - 3 * p2[ 0 ] + p3[ 0 ] ) * t3 ),
        0.5 * ( ( 2 * p1[ 1 ] ) + ( -p0[ 1 ] + p2[ 1 ] ) * t
          + ( 2 * p0[ 1 ] - 5 * p1[ 1 ] + 4 * p2[ 1 ] - p3[ 1 ] ) * t2
          + ( -p0[ 1 ] + 3 * p1[ 1 ] - 3 * p2[ 1 ] + p3[ 1 ] ) * t3 ),
      ] );
    }
  }
  return out;
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;
/** The cup: where it sits and how far it reaches. */
const CUP_TOP = 24, CUP_BOT = 76, CUP_HALF = 25;
/** Where the stem meets the ground. */
const FOOT = 114;

/**
 * THE CUP — three petals, two notches.
 *
 * The notch control points are DOUBLED so the Catmull-Rom keeps them reasonably
 * sharp: run through single points the curve rounds them off and the three petals
 * merge into one dome, which is a crocus.
 */
export const FLOWER_HEAD = poly( smooth( [
  [ CX, CUP_BOT ],                              // the cup's foot, on the stem
  [ CX - CUP_HALF * 0.86, CUP_BOT - 12 ],       // up the left side, drawing IN
  [ CX - CUP_HALF, CUP_BOT - 30 ],
  [ CX - CUP_HALF * 0.82, CUP_TOP + 8 ],        // the left petal's tip
  [ CX - 9, CUP_TOP + 20 ],                     // the notch
  [ CX - 9, CUP_TOP + 20 ],
  [ CX, CUP_TOP ],                              // the centre petal, tallest
  [ CX + 9, CUP_TOP + 20 ],                     // the second notch
  [ CX + 9, CUP_TOP + 20 ],
  [ CX + CUP_HALF * 0.82, CUP_TOP + 8 ],        // the right petal's tip
  [ CX + CUP_HALF, CUP_BOT - 30 ],
  [ CX + CUP_HALF * 0.86, CUP_BOT - 12 ],
], 7 ) );

/**
 * The two SIDE PETALS as their own shape, for the lit state.
 *
 * A tulip is not one flat colour: the outer petals turn away from the light and
 * the centre one faces it. Drawing the pair a shade deeper is the whole of the
 * modelling, and it is what stops the cup reading as a balloon.
 */
export const FLOWER_SHADE = ( () => {
  const side = ( s: number ): string => poly( smooth( [
    [ CX + s * 2, CUP_BOT - 4 ],
    [ CX + s * CUP_HALF * 0.86, CUP_BOT - 12 ],
    [ CX + s * CUP_HALF, CUP_BOT - 30 ],
    [ CX + s * CUP_HALF * 0.82, CUP_TOP + 8 ],
    [ CX + s * 9, CUP_TOP + 20 ],
  ], 7 ) );
  return `${ side( -1 ) } ${ side( 1 ) }`;
} )();

/**
 * The STEM: straight, the straightest thing in the drawing, and EXACTLY CENTRED.
 *
 * It used to be half a unit off (2.8 one side, 3.4 the other) from being written
 * by hand, which is invisible on its own and very visible once the cup sits on top
 * of it — the owner read the flower as growing out of the side of its own stalk.
 * Symmetric now, and the leaves below are mirrored about this same axis.
 *
 * `top` is where it meets the cup. Two callers want two different answers, so it
 * is a parameter rather than a constant — see FLOWER_FOLIAGE_JOINED.
 */
const stem = ( top: number ): string => poly( [
  [ CX - 3.1, top ], [ CX - 3.1, FOOT ], [ CX + 3.1, FOOT ], [ CX + 3.1, top ],
] );

/** The stem as the solid drawings want it: run UNDER the cup by six units, so no
 *  rasteriser can leave a hairline where the two meet. */
export const FLOWER_STEM = stem( CUP_BOT - 6 );

/**
 * ONE BLADE LEAF, and the chat wallpaper's whole grain.
 *
 * Drawn from its own stalk at the origin so a caller can place and turn it
 * freely. POINTED at the tip and asymmetric about its midline: the wallpaper drew
 * a plain almond for a pass and the owner's read was that it looked cheap. A leaf
 * without a tip is a lens, and the tip is most of the word.
 */
export const FLOWER_LEAF = ( () => {
  const pts: [ number, number ][] = [];
  const N = 22, L = 44, W = 11;
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    pts.push( [ t * L, -W * Math.sin( Math.pow( t, 0.62 ) * Math.PI ) ] );
  }
  for ( let i = N; i >= 0; i-- ) {
    const t = i / N;
    pts.push( [ t * L, W * 0.78 * Math.sin( Math.pow( t, 1.35 ) * Math.PI ) ] );
  }
  return poly( pts );
} )();

/** The two leaves in place on the plant, sweeping out and up from the base. */
export const FLOWER_LEAVES = ( () => {
  /** One blade's OUTLINE as points, so the pair can be built by reflection. */
  const bladePts = ( ang: number, len: number, wide: number, x0: number ): [ number, number ][] => {
    const ca = Math.cos( ang ), sa = Math.sin( ang );
    const pts: [ number, number ][] = [];
    const N = 22;
    const add = ( t: number, w: number ): void => {
      // the blade also BENDS as it goes, which is what a tulip leaf does and what
      // keeps the pair from reading as two straight knives
      const bend = Math.pow( t, 2 ) * 12 * Math.sign( ca );
      pts.push( [ CX + x0 + ( t * len ) * ca - w * sa + bend, FOOT - 2 + ( t * len ) * sa + w * ca ] );
    };
    for ( let i = 0; i <= N; i++ ) { const t = i / N; add( t, -wide * Math.sin( Math.pow( t, 0.62 ) * Math.PI ) ); }
    for ( let i = N; i >= 0; i-- ) { const t = i / N; add( t, wide * 0.62 * Math.sin( Math.pow( t, 1.35 ) * Math.PI ) ); }
    return pts;
  };

  /**
   * THE RIGHT LEAF IS THE LEFT ONE REFLECTED, point for point, about the stem's
   * axis. Not a second blade at the mirrored ANGLE — that is what this replaced,
   * twice, and it is not the same thing.
   *
   * A blade is fat on one side and thin on the other: the two edges run through
   * `sin(t^0.62)` and `sin(t^1.35)`, and those are defined in the blade's OWN
   * frame. Give the second blade the mirrored direction and that built-in
   * asymmetry comes along unmirrored, so the fat edge lands on the outside of one
   * leaf and the inside of the other. The angles were right and the pair still did
   * not match, which is exactly what the owner saw: "soldaki yaprak iyi, sağdaki
   * de öyle olsun."
   *
   * Reflecting the finished outline carries the profile, the bend and the offset
   * across together. The two leaves are now the same leaf, and they sit the same
   * distance out from a stem that runs down the middle of both.
   *
   * poly() puts the winding back: mirroring reverses it, and a reversed subpath
   * inside a nonzero fill is a hole.
   */
  const left = bladePts( -2.22, 50, 9, -2.4 );
  const right: [ number, number ][] = left.map( ( [ x, y ] ) => [ 2 * CX - x, y ] );
  return `${ poly( left ) } ${ poly( right ) }`;
} )();

/** The FOLIAGE — stem and both leaves — as one path, so a caller can give it its
 *  own colour independently of the cup.
 *
 *  ONE path rather than three matters: fill-opacity applies to the path, not to
 *  each subpath, so the leaves may cross the stem without the crossing going
 *  darker. Split them up and every translucent plant grows a bruise. */
export const FLOWER_FOLIAGE = `${ FLOWER_LEAVES } ${ FLOWER_STEM }`;

/**
 * The same foliage with the stem stopping EXACTLY at the cup's foot.
 *
 * For the chat wallpaper, where the cup is a separate translucent path: run the
 * stem six units up under it, as the solid drawings do, and those six units show
 * straight through the petal as a dark spine (owner 2026-08-16: "taç yaprak dalın
 * üzerinde olmasın, tam bitişinde olsun, çünkü yarı saydam yaptığın için altı
 * görünüyor").
 *
 * They meet at (CX, CUP_BOT), which is the cup's own foot point — so this butts
 * against the petal exactly, with nothing behind it and no gap in front of it.
 */
export const FLOWER_FOLIAGE_JOINED = `${ FLOWER_LEAVES } ${ stem( CUP_BOT ) }`;

/**
 * The green every tulip's foliage takes, whatever colour its cup is (owner
 * 2026-08-16): a bed of tulips is four flower colours on ONE kind of plant, and
 * letting the leaves follow the bloom made four different species.
 *
 * It briefly matched the second passcode digit, which is where the value came
 * from. That digit is orange now, and the coincidence is gone — this is simply the
 * theme's foliage green, and it should not be chased back into the ramp.
 *
 * Fixed rather than derived from the cup tone, for the same reason every other
 * fixed colour in this family is: derive it and it collapses into the bloom the
 * moment that tone moves.
 */
export const FLOWER_GREEN = '#3C8F63';

/** The whole plant as ONE path — leaves, stem and cup. This is what the idle tab
 *  and the unentered passcode dot draw, where the whole silhouette is one flat
 *  tone. Every subpath is clockwise, so the nonzero fill unions them instead of
 *  punching holes at the joints. */
export const FLOWER = `${ FLOWER_FOLIAGE } ${ FLOWER_HEAD }`;

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered tulips, on the WHITE upper zone this skin's passcode screen
 * paints. DARK, like every light skin in this family.
 *
 * A BED OF TULIPS IS FOUR COLOURS, which is the one place this subject pays for
 * itself twice: the ramp had to be four distinct hues anyway, and a tulip is the
 * flower people actually plant that way.
 *
 * REORDERED into their own lightness order and re-spaced: lilac, ORANGE,
 * cornflower blue, rose at 3.18 / 3.79 / 4.78 / 5.55. It used to rise, DIP almost
 * to the unentered dot on the second digit, and rise again — a row with a hole in
 * it reads as a digit that failed to register.
 *
 * Two of the four are worth stating. The BLUE, because re-spacing first landed on
 * lilac, green, PURPLE, rose — correct by every number and wrong on the screen,
 * since a lilac and a purple two stops apart are the same colour twice at that
 * size. And the ORANGE, which replaced a meadow green (owner 2026-08-16): with the
 * foliage now fixed green on every digit, a green BLOOM in the row read as a tulip
 * that had not opened rather than as a colour of its own.
 */
export const FLOWER_PC_COLORS = [ '#A87FD0', '#C86A1C', '#4A72B8', '#A34D5F' ];

/**
 * ONE tone for every unentered tulip — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.50:1 on the white zone: present, and plainly a flower nobody has picked yet.
 */
export const FLOWER_PC_OFF = '#C3D9BC';

/**
 * A per-digit lean, alternating side.
 *
 * WIDER than the bellflower's, which had to stay tight because a nodding bell
 * turned too far reads as a broken stem. A tulip leans in wind and comes back —
 * the lean is free to do what it is for, which is to stop four identical drawings
 * reading as a printed row.
 */
export const FLOWER_PC_TILTS = [ -14, 10, -7, 16 ];
